import React from 'react';
import { CheckCircle, Loader2, AlertCircle, Clock } from 'lucide-react';

/**
 * Auto-Save Indicator Component
 *
 * Shows current save status:
 * - idle: No changes
 * - saving: Currently saving
 * - saved: Successfully saved
 * - error: Save failed
 */
const AutoSaveIndicator = ({ saveStatus, lastSaved }) => {
    // Don't show anything if idle
    if (saveStatus === 'idle') return null;

    // Format last saved time
    const formatLastSaved = (date) => {
        if (!date) return '';

        const now = new Date();
        const diff = Math.floor((now - new Date(date)) / 1000); // seconds

        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return new Date(date).toLocaleTimeString();
    };

    // Status configurations
    const statusConfig = {
        saving: {
            icon: Loader2,
            text: 'Saving...',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-700',
            borderColor: 'border-blue-200',
            iconClass: 'animate-spin'
        },
        saved: {
            icon: CheckCircle,
            text: `Saved ${formatLastSaved(lastSaved)}`,
            bgColor: 'bg-green-50',
            textColor: 'text-green-700',
            borderColor: 'border-green-200',
            iconClass: ''
        },
        error: {
            icon: AlertCircle,
            text: 'Save failed',
            bgColor: 'bg-red-50',
            textColor: 'text-red-700',
            borderColor: 'border-red-200',
            iconClass: ''
        }
    };

    const config = statusConfig[saveStatus];
    if (!config) return null;

    const Icon = config.icon;

    return (
        <div
            className={`fixed top-4 right-4 z-50 ${config.bgColor} ${config.textColor} px-4 py-2.5 border ${config.borderColor} shadow-md flex items-center gap-2.5 animate-in slide-in-from-top-4 duration-300`}
            style={{
                borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-primary)',
                fontSize: '0.875rem',
                fontWeight: '500'
            }}
        >
            <Icon className={`w-4 h-4 ${config.iconClass}`} />
            <span>{config.text}</span>
        </div>
    );
};

export default AutoSaveIndicator;
